import React, { useState } from 'react';
import { mapValues } from 'lodash';
import type { IconType } from 'admin/components/common/Icon';
import BindControl from 'admin/components/pages/Story/CardEditor/Inspector/PropField/BindControl';
import RowAction from 'admin/components/pages/Story/CardEditor/Inspector/PropField/RowAction';
import { PROP_FIELD_ICONS } from 'admin/components/pages/Story/CardEditor/Inspector/config/prop-fields-config';
import {
	getDesignPresetInheritedAssignment,
	getDesignPresetTokenId,
	type DesignDefinition,
	type DesignPreset,
	type DesignPresetProperty,
	type DesignPresetPropertyValue,
	type DesignPresetValue,
} from 'common/design-tokens';
import {
	DESIGN_PRESET_LINE_STYLES,
	collapseCssSides,
	expandCssSides,
	getBorderLineStyle,
	hasBorder,
	isPerSideBorderStyle,
} from 'common/design-tokens/borders';
import type { StoryDefaultPlatform, StoryMediaQuery } from 'types/story';
import { getDesignPresetDisplayValue, getDesignPresetPlatformName } from './design-preset-display';
import type { PresetAssignmentChange, PresetPropertyGroup } from './design-preset-editing';
import { getPresetSpacingAssignment } from './design-preset-spacing';
import AssignmentCell, { PlatformAssignment } from './DesignPresetAssignment';
import css from './DesignPresetsEditor.scss';

export type GroupEditorContext = {
	preset: DesignPreset;
	platform: 'base' | StoryDefaultPlatform;
	design: DesignDefinition;
	mediaQuery: StoryMediaQuery;
	modeSelection: Record<string, string>;
	fonts: { fontFamily: string }[];
	disabled?: boolean;
	onChange: (changes: readonly PresetAssignmentChange[]) => void;
};

type Read = {
	assigned: DesignPresetValue | undefined;
	inherited?: { value: DesignPresetValue; source: string };
	display: ReturnType<typeof getDesignPresetDisplayValue>;
};

/** A value at this platform, as a cell shows it: assigned here, or inherited with its platform's name */
function readAssignment(
	ctx: GroupEditorContext,
	property: DesignPresetProperty,
	assignment?: DesignPresetPropertyValue
) {
	const value = assignment ?? ctx.preset.properties[property] ?? {};
	const inherited =
		ctx.platform === 'base' ? undefined : getDesignPresetInheritedAssignment(value, ctx.platform, ctx.mediaQuery);
	return {
		assigned: ctx.platform === 'base' ? value.base : value.platforms?.[ctx.platform],
		inherited: inherited && {
			value: inherited.value,
			source:
				inherited.source === 'Base'
					? 'Base'
					: getDesignPresetPlatformName(inherited.source, ctx.mediaQuery.config[inherited.source]?.uiName),
		},
		display: getDesignPresetDisplayValue({ ...ctx, property, assignment: value }),
	} satisfies Read;
}

const effective = (read: Read) => read.assigned ?? read.inherited?.value;

/** What to write here to keep a value as shown: a token as it is, a literal as its px on this platform */
function toWritable(read: Read): DesignPresetValue | undefined {
	const value = effective(read);
	if (value === undefined || getDesignPresetTokenId(value)) return value;
	return read.display?.value ?? value;
}

/** A label and its bind link; a `<label>`, as in the Inspector, so a click on it toggles the link's checkbox */
function EditorLabel({ label, bind }: { label: string; bind?: React.ReactNode }) {
	return bind ? (
		<label className={css.editorLabel}>
			{label}
			{bind}
		</label>
	) : (
		<span className={css.editorLabel}>{label}</span>
	);
}

function EditorRow({ label, bind, action }: { label: string; bind?: React.ReactNode; action?: React.ReactNode }) {
	return (
		<div className={css.editorRow}>
			<EditorLabel label={label} bind={bind} />
			{action}
		</div>
	);
}

/** What every field of a group shares: an Inspector field, with the token button over its icon */
const fieldContext = (ctx: GroupEditorContext) => ({
	design: ctx.design,
	fonts: ctx.fonts,
	modeSelection: ctx.modeSelection,
	disabled: ctx.disabled,
	isBase: ctx.platform === 'base',
	variant: 'field' as const,
});

/** The Inspector's Space group: bound, one field per axis; unbound, four sides */
export function SpaceGroup({ group, ...ctx }: GroupEditorContext & { group: PresetPropertyGroup }) {
	const side = (suffix: string) => group.properties.find(property => property.endsWith(suffix))!;
	const [left, right, top, bottom] = ['Left', 'Right', 'Top', 'Bottom'].map(side);
	const shared = (properties: DesignPresetProperty[]) => getPresetSpacingAssignment({ ...ctx, properties });
	const x = shared([left, right]);
	const y = shared([top, bottom]);
	const equal = !x.mixed && !y.mixed;
	const [bound, setBound] = useState(equal);
	const isBound = bound && equal;
	const field = (property: DesignPresetProperty, icon: IconType) => (
		<PlatformAssignment
			key={property}
			{...ctx}
			property={property}
			variant="field"
			icon={icon}
			onChange={value => ctx.onChange([{ property, value }])}
		/>
	);
	// Joining pairs right with left and bottom with top, as the Inspector does
	const bind = (next: boolean) => {
		setBound(next);
		if (next && !equal)
			ctx.onChange([
				{ property: right, value: toWritable(readAssignment(ctx, left)) },
				{ property: bottom, value: toWritable(readAssignment(ctx, top)) },
			]);
	};
	const axis = (value: typeof x, properties: DesignPresetProperty[], icon: IconType, label: string) => (
		<AssignmentCell
			{...value}
			{...fieldContext(ctx)}
			icon={icon}
			property={properties[0]}
			assignmentLabel={label}
			onChange={next => ctx.onChange(properties.map(property => ({ property, value: next })))}
		/>
	);
	return (
		<>
			<EditorRow label={group.label} bind={<BindControl checked={isBound} onChange={bind} />} />
			{isBound ? (
				<div className={css.fieldGrid}>
					{axis(x, [left, right], PROP_FIELD_ICONS.SPACE_X, 'Left and right')}
					{axis(y, [top, bottom], PROP_FIELD_ICONS.SPACE_Y, 'Top and bottom')}
				</div>
			) : (
				<div className={css.fieldGrid}>
					{field(left, PROP_FIELD_ICONS.SPACE_LEFT)}
					{field(right, PROP_FIELD_ICONS.SPACE_RIGHT)}
					{field(top, PROP_FIELD_ICONS.SPACE_TOP)}
					{field(bottom, PROP_FIELD_ICONS.SPACE_BOTTOM)}
				</div>
			)}
		</>
	);
}

const CORNERS: { property: DesignPresetProperty; icon: IconType }[] = [
	{ property: 'borderTopLeftRadius', icon: PROP_FIELD_ICONS.RADIUS_TL },
	{ property: 'borderTopRightRadius', icon: PROP_FIELD_ICONS.RADIUS_TR },
	{ property: 'borderBottomLeftRadius', icon: PROP_FIELD_ICONS.RADIUS_BL },
	{ property: 'borderBottomRightRadius', icon: PROP_FIELD_ICONS.RADIUS_BR },
];

/** The Inspector's Radius: bound, one value on the label row; unbound, a row of four corners */
export function RadiusGroup(ctx: GroupEditorContext) {
	const corners = CORNERS.map(corner => corner.property);
	const shared = getPresetSpacingAssignment({ ...ctx, properties: corners });
	const cornersSet = corners.some(property => effective(readAssignment(ctx, property)) !== undefined);
	const [bound, setBound] = useState(!shared.mixed);
	const isBound = bound && !shared.mixed;
	const bind = (next: boolean) => {
		setBound(next);
		if (next && shared.mixed) {
			const first = corners
				.map(property => readAssignment(ctx, property))
				.find(read => effective(read) !== undefined);
			const value = first ? toWritable(first) : undefined;
			ctx.onChange(corners.map(property => ({ property, value })));
		}
		// Unbinding a single radius writes it to the four corners, as the Inspector does
		if (!next && !cornersSet) {
			const value = toWritable(readAssignment(ctx, 'borderRadius'));
			if (value !== undefined)
				ctx.onChange([
					...corners.map(property => ({ property, value })),
					{ property: 'borderRadius', value: undefined },
				]);
		}
	};
	const boundField = cornersSet ? (
		<AssignmentCell
			{...shared}
			{...fieldContext(ctx)}
			property={corners[0]}
			assignmentLabel="All corners"
			onChange={value => ctx.onChange(corners.map(property => ({ property, value })))}
		/>
	) : (
		<PlatformAssignment
			{...ctx}
			property="borderRadius"
			variant="field"
			onChange={value => ctx.onChange([{ property: 'borderRadius', value }])}
		/>
	);
	return (
		<>
			<EditorRow
				label="Radius"
				bind={<BindControl checked={isBound} onChange={bind} />}
				action={isBound ? <span className={css.editorValue}>{boundField}</span> : null}
			/>
			{isBound ? null : (
				<div className={css.fieldRow4}>
					{CORNERS.map(({ property, icon }) => (
						<PlatformAssignment
							key={property}
							{...ctx}
							property={property}
							variant="field"
							icon={icon}
							onChange={value => ctx.onChange([{ property, value }])}
						/>
					))}
				</div>
			)}
		</>
	);
}

const SIDE_ICONS: IconType[] = [
	PROP_FIELD_ICONS.BORDER_TOP,
	PROP_FIELD_ICONS.BORDER_RIGHT,
	PROP_FIELD_ICONS.BORDER_BOTTOM,
	PROP_FIELD_ICONS.BORDER_LEFT,
];
const SIDE_NAMES = ['Top', 'Right', 'Bottom', 'Left'];
const LINE_STYLE_OPTIONS = DESIGN_PRESET_LINE_STYLES.map(value => ({
	value,
	label: (
		<span className={css.styleOption}>
			<i className={css.lineGlyph} data-style={value} />
			{value[0].toUpperCase() + value.slice(1)}
		</span>
	),
}));
const DEFAULT_BORDER = { width: '1px', color: 'rgba(0, 0, 0, 1)' };

/**
 * The Inspector's Border group: ⊕ / ⊖ at the row end, Width · Color · Style, and Width bound to one value or split
 * into four sides. A side at 0 is not drawn; an older per-side style folds into the widths on the next write.
 */
export function BorderGroup(ctx: GroupEditorContext) {
	const style = readAssignment(ctx, 'borderStyle');
	const width = readAssignment(ctx, 'borderWidth');
	const color = readAssignment(ctx, 'borderColor');
	const styleValue = effective(style);
	const on = hasBorder(styleValue);
	const lineStyle = getBorderLineStyle(styleValue);
	const perSide = isPerSideBorderStyle(styleValue);
	const styleSides = expandCssSides(styleValue);
	const widthAssignment = ctx.preset.properties.borderWidth ?? {};
	const sideOf = (value: DesignPresetValue, index: number) => expandCssSides(value)[index];
	// Each side read as its own assignment, so it inherits and converts per platform like any field
	const sides = [0, 1, 2, 3].map(index => {
		const assignment: DesignPresetPropertyValue = {
			...(widthAssignment.base !== undefined ? { base: sideOf(widthAssignment.base, index) } : {}),
			...(widthAssignment.platforms
				? {
						platforms: mapValues(widthAssignment.platforms, value =>
							value === undefined ? value : sideOf(value, index)
						),
					}
				: {}),
		};
		const read: Read = readAssignment(ctx, 'borderWidth', assignment);
		if (!perSide || styleSides[index] !== 'none') return read;
		const zero = '0';
		return {
			assigned: read.assigned === undefined ? undefined : zero,
			inherited: read.inherited && { ...read.inherited, value: zero },
			display: { value: zero, cssValue: zero, authoredValue: zero },
		};
	});
	const parts = sides.map(read => String(toWritable(read) ?? '0'));
	// Bound only when one stored width shows on every side; an older per-side style can zero sides of a single width
	const equal =
		parts.every(part => part === parts[0]) && !/\s/.test(String(effective(width) ?? '').trim()) && !perSide;
	const [bound, setBound] = useState(equal);
	const isBound = bound && equal;
	const writeWidths = (next: string[], lineStyleValue: string = lineStyle) =>
		ctx.onChange([
			{ property: 'borderWidth', value: collapseCssSides(next) },
			...(perSide ? [{ property: 'borderStyle' as const, value: lineStyleValue }] : []),
		]);
	const bindWidth = (next: boolean) => {
		setBound(next);
		if (!next || equal) return;
		const widest = parts.reduce((max, part) => (Number.parseFloat(part) > Number.parseFloat(max) ? part : max));
		writeWidths([widest, widest, widest, widest]);
	};
	const add = () =>
		ctx.onChange([
			{ property: 'borderStyle', value: 'solid' },
			...(effective(width) === undefined
				? [{ property: 'borderWidth' as const, value: DEFAULT_BORDER.width }]
				: []),
			...(effective(color) === undefined
				? [{ property: 'borderColor' as const, value: DEFAULT_BORDER.color }]
				: []),
		]);
	// On Base the border goes; on a platform that inherits one, `none` turns it off there only
	const remove = () =>
		ctx.onChange([
			{ property: 'borderStyle', value: ctx.platform === 'base' ? undefined : 'none' },
			{ property: 'borderWidth', value: undefined },
			{ property: 'borderColor', value: undefined },
		]);
	const lineStyleRead = (value?: DesignPresetValue) => (value === undefined ? undefined : getBorderLineStyle(value));
	return (
		<>
			<EditorRow
				label="Border"
				action={
					<RowAction
						action={on ? 'remove' : 'add'}
						label={on ? 'Remove border' : 'Add border'}
						disabled={ctx.disabled}
						onClick={on ? remove : add}
					/>
				}
			/>
			{on ? (
				<>
					<div className={css.editorColumns}>
						<div className={css.editorColumn}>
							<EditorLabel label="Width" bind={<BindControl checked={isBound} onChange={bindWidth} />} />
							{isBound ? (
								<PlatformAssignment
									{...ctx}
									property="borderWidth"
									variant="field"
									onChange={value => ctx.onChange([{ property: 'borderWidth', value }])}
								/>
							) : (
								<span className={css.sidesHint}>Sides</span>
							)}
						</div>
						<div className={css.editorColumn}>
							<EditorLabel label="Color" />
							<PlatformAssignment
								{...ctx}
								property="borderColor"
								variant="field"
								onChange={value => ctx.onChange([{ property: 'borderColor', value }])}
							/>
						</div>
						<div className={css.editorColumn}>
							<EditorLabel label="Style" />
							<AssignmentCell
								{...fieldContext(ctx)}
								property="borderStyle"
								assigned={lineStyleRead(style.assigned)}
								inherited={style.inherited && { ...style.inherited, value: lineStyle }}
								display={undefined}
								enumOptions={LINE_STYLE_OPTIONS}
								onChange={value =>
									value === undefined
										? ctx.onChange([{ property: 'borderStyle', value: undefined }])
										: perSide
											? writeWidths(parts, String(value))
											: ctx.onChange([{ property: 'borderStyle', value }])
								}
							/>
						</div>
					</div>
					{isBound ? null : (
						<div className={css.fieldRow4}>
							{sides.map((read, index) => (
								<AssignmentCell
									key={SIDE_NAMES[index]}
									{...read}
									{...fieldContext(ctx)}
									icon={SIDE_ICONS[index]}
									property="borderWidth"
									assignmentLabel={`${SIDE_NAMES[index]} border width`}
									onChange={value => {
										if (value !== undefined)
											writeWidths(parts.map((part, i) => (i === index ? String(value) : part)));
									}}
								/>
							))}
						</div>
					)}
				</>
			) : null}
		</>
	);
}
